import React, { useEffect, useState, useContext, useRef } from "react";
import NeededByField from "components/NeededByField";
import { formatDateOnly } from "helpers/dateOnly";
import { useParams, useSearchParams } from "react-router-dom";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge } from "theme-ui";
import { BackArrowIcon, PencilIcon, QuestionMarkIcon, TrashIcon, NotesIcon, PurchaseOrderIcon, OverviewIcon, PhotosIcon, FilesIcon, ProvidersIcon } from "components/svg";
import H2 from "components/typography/H2";
import { themevals } from "theme/themevals";
import H5 from "components/typography/H5";
import Overview from "./components/Overview";
import Notes from "./components/Notes";
import UserContext from "context/User";
import { getMyCompany } from "api/Companies";
import Files from "./components/Files";
import Photos from "./components/Photos";
import Dispatches from "./components/Dispatches";
import { getProject, updateProject, getProjectStatusByName, flushClientInput } from "api/Projects";
import { getCorrigoWorkOrderForRfq } from "api/QuoteRequests";
import { trackActivity } from "api/ActivityTracker";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import moment from "moment-timezone";
import BasicModal from "components/ui/BasicModal";
import { Modal, ModalHeader, ModalBody } from "components/ui/Modal";
import PurchaseOrders from "./components/PurchaseOrders";
import ProjectStageBar from "./components/ProjectStageBar";
import SiteMapSection from "components/SiteMapSection";
import SnapshotSelectionDisplay from "components/SnapshotSelectionDisplay";
import UpdateClientPanel from "./components/UpdateClientPanel";
import MaintenanceContextBanner from "components/MaintenanceContextBanner";
import MaintenanceProjectCard from "containers/MaintenanceOpportunities/ProjectCard";

const Project = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  useEffect(() => {
    const tab = searchParams.get("tab") || "overview";
    setActiveTab(tab);
  }, [searchParams]);
  const { user } = useContext<any>(UserContext);
  const [company, setCompany] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState<boolean>(false);
  // Update Client (CorrigoPro status paste) — shown only when this
  // project sits on a Corrigo-managed (work_order_fed) maintenance plan
  // AND a work order produced it. The backend collapses those three
  // conditions into one existence check; we hold its result here and
  // open the composer in a modal (the panel was previously crammed into
  // the 250px left rail, where its textarea was unusable).
  const [corrigoWo, setCorrigoWo] = useState<{ hasCorrigoWorkOrder: boolean; workOrderId: string | null; servicePlanId: string | null }>({
    hasCorrigoWorkOrder: false,
    workOrderId: null,
    servicePlanId: null,
  });
  const [updateClientModalOpen, setUpdateClientModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const hasTrackedView = useRef(false);

  const { id } = useParams();

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Project";
    hasTrackedView.current = false;
    _init();
  }, [id]);

  // Client visit ends (leaves this project, or hides / closes the tab):
  // send what they added as ONE Slack digest (2026-10-12). The backend
  // only sends when something is pending, so this is a no-op otherwise.
  useEffect(() => {
    const isClientUser = user?.accessLevel === "isClient" || user?.accessLevel === "isClientAdmin";
    if (!isClientUser || !id) return;
    const onHide = () => {
      if (document.visibilityState === "hidden") flushClientInput(id, true);
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      flushClientInput(id);
    };
  }, [id, user?.accessLevel]);

  const _init = async () => {
    if (user.role !== null) {
      const res = await getMyCompany();
      if (res.id) {
        setCompany(res);
      }
    }

    const res = await getProject({ data: { id } });
    if (res.id) {
      setProject(res);
      // Update Client gate: only staff, and only when the project came
      // from a plan-backed RFQ, are eligible; the backend confirms the
      // plan is Corrigo-managed AND a work order exists. Reset first so a
      // navigation between projects never leaves a stale button showing.
      setCorrigoWo({ hasCorrigoWorkOrder: false, workOrderId: null, servicePlanId: null });
      if (user.accessLevel === "isCompany" && res.originQuoteRequest?.id) {
        const cw = await getCorrigoWorkOrderForRfq({ rfqId: res.originQuoteRequest.id });
        setCorrigoWo({
          hasCorrigoWorkOrder: !!cw?.hasCorrigoWorkOrder,
          workOrderId: cw?.workOrderId ?? null,
          servicePlanId: cw?.servicePlanId ?? null,
        });
      }
      // Seed the client pickup input from the loaded project.
      {
        const a = res.supplySourceAddress || {};
        setClientPickupInput([a.line1, a.city, a.state, a.postalCode].filter(Boolean).join(", "));
      }

      // Track view after successful load
      if (!hasTrackedView.current) {
        hasTrackedView.current = true;
        void trackActivity({
          module: "projects",
          action: "view_project_detail",
          actionType: "view",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "project",
          entityId: res.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }
  };

  const _confirmCancelProject = () => {
    setConfirmCancelModalOpen(true);
  };

  const resetProject = (project: any) => {
    setProject(project);
  };

  // Client (Fibar) self-serve: set the pickup source address on an install-only
  // pickup project, before it is scheduled. Sends ONLY supplySourceAddress; the
  // backend also field-scopes client updates so nothing else can be changed.
  const [clientPickupInput, setClientPickupInput] = useState<string>("");
  const [savingClientPickup, setSavingClientPickup] = useState<boolean>(false);
  const saveClientPickup = async () => {
    if (!project?.id) return;
    setSavingClientPickup(true);
    const raw = clientPickupInput.trim();
    const response = await updateProject({
      data: { id: project.id },
      update: { supplySourceAddress: raw ? { line1: raw } : null },
    });
    if (response?.id) resetProject(response);
    setSavingClientPickup(false);
  };

  const _cancelConfirmModal = () => {
    setConfirmCancelModalOpen(false);
  };

  const _submitCancelProject = async () => {
    setSubmitting(true);

    const res = await getProjectStatusByName({ data: { name: "Cancelled" } });
    if (res.id) {
      const response = await updateProject({ data: { id: project.id }, update: { status: { id: res.id } } });
      if (response) {
        setSubmitting(false);
        setConfirmCancelModalOpen(false);
        resetProject(response);
      }
    }
  };

  return (
    <>
      <Header></Header>
      <Page
        backAction={
          <Link variant="back" href="/projects">
            <BackArrowIcon />
            {`Back to ${"Projects"}`}
          </Link>
        }
        $noPadding={true}
      >
        {project == null ? (
          <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : (
          <>
            <Flex sx={{ gap: "20px", alignItems: "center" }}>
              <H2>Project# {`GMP-${String(project.sequence).padStart(5, "0")} - ${project.projectName ? project.projectName : "Instant Estimate"}`}</H2>
            </Flex>
            <ProjectStageBar project={project} isProvider={user.accessLevel === "isProvider"} />
            <Grid gap={0} columns={[1, "250px 1fr"]}>
              <Box>
                <H5 marginTop={"30px"} marginBottom={"10px"} fontFamily={"Poppins"} color={themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500}>
                  Project Details
                </H5>
              </Box>
              <Flex sx={{ paddingLeft: 30, alignItems: "flex-end" }}>
                <Flex sx={{ gap: "40px" }}>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "overview" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      if (project?.id) {
                        void trackActivity({
                          module: "projects",
                          action: "view_project_overview",
                          actionType: "view",
                          userId: user?.id || null,
                          companyId: user?.company?.id || null,
                          entityType: "project",
                          entityId: project.id,
                          metadata: null,
                          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                        });
                      }
                      setActiveTab("overview");
                      setSearchParams({ tab: "overview" });
                    }}
                  >
                    <OverviewIcon fill={activeTab == "overview" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "overview" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Overview
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "notes" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      if (project?.id) {
                        void trackActivity({
                          module: "projects",
                          action: "view_project_notes",
                          actionType: "view",
                          userId: user?.id || null,
                          companyId: user?.company?.id || null,
                          entityType: "project",
                          entityId: project.id,
                          metadata: null,
                          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                        });
                      }
                      setActiveTab("notes");
                      setSearchParams({ tab: "notes" });
                    }}
                  >
                    <NotesIcon fill={activeTab == "notes" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "notes" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Notes
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "photos" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      if (project?.id) {
                        void trackActivity({
                          module: "projects",
                          action: "view_project_photos",
                          actionType: "view",
                          userId: user?.id || null,
                          companyId: user?.company?.id || null,
                          entityType: "project",
                          entityId: project.id,
                          metadata: null,
                          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                        });
                      }
                      setActiveTab("photos");
                      setSearchParams({ tab: "photos" });
                    }}
                  >
                    <PhotosIcon fill={activeTab == "photos" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "photos" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Photos
                    </Box>
                  </Flex>
                  <Flex
                    sx={{ paddingBottom: "10px", borderBottom: activeTab == "files" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                    onClick={() => {
                      if (project?.id) {
                        void trackActivity({
                          module: "projects",
                          action: "view_project_files",
                          actionType: "view",
                          userId: user?.id || null,
                          companyId: user?.company?.id || null,
                          entityType: "project",
                          entityId: project.id,
                          metadata: null,
                          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                        });
                      }
                      setActiveTab("files");
                      setSearchParams({ tab: "files" });
                    }}
                  >
                    <FilesIcon fill={activeTab == "files" ? "#000" : themevals.colors.dkgrey} width="14px" />
                    <Box sx={{ color: activeTab == "files" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}>
                      Files
                    </Box>
                  </Flex>
                    {user.role !== null && (
                    <Flex
                      sx={{ paddingBottom: "10px", borderBottom: activeTab == "dispatches" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                      onClick={() => {
                        setActiveTab("dispatches");
                        setSearchParams({ tab: "dispatches" });
                      }}
                    >
                      <ProvidersIcon fill={activeTab == "dispatches" ? "#000" : themevals.colors.dkgrey} width="14px" />
                      <Box
                        sx={{ color: activeTab == "dispatches" ? "#000" : themevals.colors.dkgrey, paddingLeft: "8px", fontFamily: "Poppins", fontSize: "12px", lineHeight: "14px", fontWeight: "600" }}
                      >
                        Dispatches
                      </Box>
                    </Flex>
                  )}
                  {user.accessLevel === "isProvider" && (
                    <Flex
                      sx={{ paddingBottom: "10px", borderBottom: activeTab == "purchase_orders" ? "2px solid #000" : "2px solid transparent", cursor: "pointer" }}
                      onClick={() => {
                        setActiveTab("purchase_orders");
                        setSearchParams({ tab: "purchase_orders" });
                      }}
                    >
                      <PurchaseOrderIcon fill={activeTab == "purchase_orders" ? "#000" : themevals.colors.dkgrey} width="14px" />
                      <Box
                        sx={{
                          color: activeTab == "purchase_orders" ? "#000" : themevals.colors.dkgrey,
                          paddingLeft: "8px",
                          fontFamily: "Poppins",
                          fontSize: "12px",
                          lineHeight: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Purchase Orders
                      </Box>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Grid>
            <Grid gap={0} columns={[1, "250px 1fr"]} sx={{ minHeight: "calc(100vh - 300px)" }}>
              {/* minWidth 0 + overflowWrap on the SIDEBAR COLUMN, not on
                  one field. A grid item defaults to min-width: auto, so any
                  unbreakable value — an email, a URL, a long address —
                  pushed past the 250px track and over the main panel.
                  Fixing it here covers every value in the sidebar at once. */}
              <Box sx={{ borderTop: "1px solid", borderRight: "1px solid", borderColor: "inputOutline", minWidth: 0, overflowWrap: "anywhere" }}>
                {user.accessLevel !== "isProvider" && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "20px" }}>
                    <Link
                      href={`/projects/${project.id}/edit`}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                        width: "100%", padding: "10px 0",
                        border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                        textDecoration: "none", color: themevals.colors.black, backgroundColor: "#fff",
                        fontFamily: "Poppins", fontSize: "13px", fontWeight: 500, cursor: "pointer",
                        "&:hover": { backgroundColor: "#F7F8FA", borderColor: themevals.colors.dkgrey },
                      }}
                    >
                      <PencilIcon height="13px" fill="currentColor" />
                      Edit
                    </Link>
                  </Box>
                )}
                {/* Update Client (CorrigoPro status paste) stays a rail
                    action. The "Back to work order" / "Back to plan"
                    navigation moved OUT of the rail to the shared
                    MaintenanceContextBanner at the top of the page (F9), so
                    the project matches the RFQ/Quote/Invoice/PO records
                    instead of carrying its own divergent rail buttons.
                    Same Corrigo-WO gate and button style as before. */}
                {user.accessLevel === "isCompany" && corrigoWo.hasCorrigoWorkOrder && (
                  <Box sx={{ paddingRight: "30px", paddingTop: "10px" }}>
                    <BasicButton
                      scheme="secondaryOutline"
                      type="button"
                      keepText={true}
                      styles={{ margin: "10px 0 0 0", width: "100%" }}
                      onClick={() => setUpdateClientModalOpen(true)}
                    >
                      Copy status update
                    </BasicButton>
                  </Box>
                )}
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Basic Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Status</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {(() => {
                        // Off-ladder statuses carry a fixed color so the Badge
                        // matches the stage bar: Pending = amber (caution),
                        // Cancelled = red (danger). Every other status uses its
                        // own stored project_status.color.
                        const nm = typeof project.status?.name === "string" ? project.status.name.toLowerCase() : "";
                        const statusVariant =
                          nm === "pending" ? "caution"
                          : nm === "cancelled" ? "danger"
                          : project.status.color;
                        return (
                          <Badge as="span" variant={statusVariant}>
                            {project.status.name}
                          </Badge>
                        );
                      })()}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Created At</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(project.createdAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Updated</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {moment(project.updatedAt).format("MMM D, YYYY")}
                    </P>
                  </Flex>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Updated By</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {`${project.lastUpdateBy?.firstName} ${project.lastUpdateBy?.lastName}`}
                    </P>
                  </Flex>

                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Estimated Service Date</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {project.serviceDate ? formatDateOnly(project.serviceDate) : "-"}
                    </P>
                  </Flex>

                  {/* The client's own deadline (2026-10-12), beside ours. Shown once
                      given (the Next steps card asks for it first); clients change
                      it here until the work is complete. */}
                  {project.clientNeededBy && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>{user.accessLevel === "isCompany" ? "Client Needs By" : "Date Needed By"}</Label>
                      {user.accessLevel !== "isCompany" && (!project.status?.stepNumber || project.status.stepNumber < 5) ? (
                        <Box sx={{ marginTop: "5px" }}>
                          <NeededByField projectId={project.id} neededBy={project.clientNeededBy} rfqDate={project.originQuoteRequest?.serviceDate} />
                        </Box>
                      ) : (
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {project.clientNeededBy ? formatDateOnly(project.clientNeededBy) : "-"}
                        </P>
                      )}
                    </Flex>
                  )}

                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Scheduled Date</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {project.scheduleDate ? formatDateOnly(project.scheduleDate) : "-"}
                    </P>
                  </Flex>
                  {project.clientPO && user.role !== null && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Client PO</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {project.clientPO}
                      </P>
                    </Flex>
                  )}
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Rush Order</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {project.isRushOrder ? "Yes" : "No"}
                    </P>
                  </Flex>
                </Box>
                {/* Install-only: read-only Mulch Supply display. Shows for any
                    install-only project (serviceType gate), even before a supply
                    method is set, so the team can see it needs setting. Editing
                    is on the project Edit window; this carries to the PO. */}
                {project.serviceType === "Install Only" && user.role !== null && (() => {
                  const methodLabel =
                    project.supplyMethod === "pickup_from_fibar" ? "Pickup from supplier"
                    : project.supplyMethod === "delivery_to_site" ? "Supplier delivers to site"
                    : project.supplyMethod === "delivery_to_yard" ? "Supplier delivers to yard"
                    : "Not set";
                  const addr = project.supplySourceAddress || {};
                  const addrLine = [addr.line1, addr.city, addr.state, addr.postalCode].filter(Boolean).join(", ");
                  return (
                    <>
                      <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      <Box sx={{ paddingRight: "30px" }}>
                        <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                          Mulch Supply
                        </H5>
                        <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginTop="0px" marginBottom="16px">
                          Material is supplied by a third party. Carries to the purchase order.
                        </P>
                        <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Method</Label>
                          <Box sx={{ marginTop: "5px" }}>
                            {project.supplyMethod ? (
                              <Badge as="span" variant="info">{methodLabel}</Badge>
                            ) : (
                              <P fontFamily={"Poppins"} color={themevals.colors.danger} fontSize={"14px"} fontWeight={500} marginTop={"0px"} marginBottom={"0px"}>
                                Not set - choose pickup or delivery on the edit screen
                              </P>
                            )}
                          </Box>
                        </Flex>
                        {project.supplyMethod === "pickup_from_fibar" && (
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Supplier pickup location</Label>
                            <P fontFamily={"Poppins"} color={addrLine ? themevals.colors.black : themevals.colors.danger} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {addrLine || "Not set - required before the PO is sent"}
                            </P>
                          </Flex>
                        )}
                        {project.supplyMethod === "delivery_to_yard" && (
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Installer yard location</Label>
                            <P fontFamily={"Poppins"} color={addrLine ? themevals.colors.black : themevals.colors.dkgrey} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {addrLine || "Defaults to the supplier depot; set a yard on the edit screen to override"}
                            </P>
                          </Flex>
                        )}
                        {project.supplyMethod === "delivery_to_site" && (
                          <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                            <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>On-site loading</Label>
                            <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                              {project.onSiteLoadingRequired ? "Required" : "Not required"}
                            </P>
                          </Flex>
                        )}
                      </Box>
                    </>
                  );
                })()}
                {/* Client (Fibar) self-serve pickup address. Shows only to a
                    client viewer on an install-only + pickup project, before it
                    is scheduled. Lets the client provide the address of the
                    location the mulch is picked up from, so our installer knows
                    where to collect it before installing at the job site. The
                    backend field-scopes client updates to this field only. */}
                {project.serviceType === "Install Only" &&
                  project.supplyMethod === "pickup_from_fibar" &&
                  user.role === null &&
                  user.accessLevel !== "isProvider" &&
                  (!project.status?.stepNumber || project.status.stepNumber < 4) && (
                    <>
                      <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      <Box sx={{ paddingRight: "30px" }}>
                        <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                          Mulch Pickup Location
                        </H5>
                        <P fontFamily="Poppins" fontSize="12px" color={themevals.colors.dkgrey} marginTop="0px" marginBottom="16px">
                          Our installer will collect the mulch from this location before installing it at the job site. Please provide the pickup address.
                        </P>
                        <Flex sx={{ flexDirection: "column", marginBottom: "12px" }}>
                          <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Pickup address</Label>
                          <input
                            type="text"
                            value={clientPickupInput}
                            placeholder="Street, city, state, ZIP"
                            onChange={(e) => setClientPickupInput(e.target.value)}
                            style={{
                              marginTop: "5px", width: "100%", padding: "8px 10px",
                              fontFamily: "Poppins", fontSize: "13px",
                              border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px",
                            }}
                          />
                        </Flex>
                        <BasicButton
                          scheme="secondaryOutline"
                          type="button"
                          $submitting={savingClientPickup}
                          keepText={true}
                          onClick={saveClientPickup}
                        >
                          Save pickup location
                        </BasicButton>
                      </Box>
                    </>
                  )}
                <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                <Box sx={{ paddingRight: "30px" }}>
                  <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                    Site Info
                  </H5>
                  <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Address</Label>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {project.location?.address?.line1 && project.location?.address?.line1 !== project.location?.address?.city
                        ? project.location?.address?.line1 + (project.location?.address?.line2 ? `, ${project.location.address?.line2}` : "")
                        : project.location?.address?.line2
                        ? project.location?.address?.line2
                        : ""}
                    </P>
                    <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                      {project.location?.address?.city}, {project.location?.address?.state}
                      {project.location?.address?.postalCode && project.location?.address?.postalCode !== "null" ? ` ${project.location?.address?.postalCode}` : ""}
                    </P>
                  </Flex>
                  {project.siteContactName && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Name</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {project.siteContactName}
                      </P>
                    </Flex>
                  )}
                  {project.siteContactPhone && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Phone</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {project.siteContactPhone}
                      </P>
                    </Flex>
                  )}
                  {project.siteContactEmail && (
                    <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                      <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Site Contact Email</Label>
                      <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                        {project.siteContactEmail}
                      </P>
                    </Flex>
                  )}
                </Box>
                {project.location?.id && user.accessLevel === "isCompany" && (
                  <>
                    {/*
                     * Session 30: only company users see the editable
                     * SiteMapSection on Projects. Clients see snapshots
                     * inherited from the origin Quote (see below).
                     * Providers see nothing here — they interact with
                     * the Project via the Purchase Order they were
                     * dispatched, not through this map surface.
                     */}
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <SiteMapSection
                      locationId={project.location.id}
                      mulchMapId={project.mulchMapId ?? null}
                      recordType="project"
                      recordId={project.id}
                      onAttachChanged={() => _init()}
                    />
                  </>
                )}
                {/*
                 * Session 30 hotfix: everyone with Project access sees
                 * snapshots inherited from the accepted (origin) Quote,
                 * if any are attached there. Projects don't carry their
                 * own snapshot column, so we resolve the display by
                 * pointing SnapshotSelectionDisplay at the Quote that
                 * spawned this Project (backend findById now populates
                 * project.originQuote for exactly this purpose).
                 * Admins see this in addition to the SiteMapSection
                 * above; clients see it standalone since SiteMapSection
                 * is gated to isCompany.
                 *
                 * Session 31 hotfix: gate this on !isProvider. The
                 * snapshot fetch endpoint hard-404s providers for any
                 * recordType other than 'purchase_order', so calling it
                 * with recordType='quote' as a provider always fails.
                 * Providers see snapshots via their PO instead.
                 */}
                {user.accessLevel !== "isProvider" &&
                  project.originQuote?.id &&
                  (project.originQuote?.mulchMapSnapshotIds?.length ?? 0) > 0 && (
                    <>
                      <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      <SnapshotSelectionDisplay
                        recordType="quote"
                        recordId={project.originQuote.id}
                        heading="Site maps included with this project"
                      />
                    </>
                  )}
                {user.accessLevel !== "isProvider" && (
                  <>
                    <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                    <Box sx={{ paddingRight: "30px" }}>
                      <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                        Contacts
                      </H5>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Primary Contact</Label>
                        <P fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={500} marginTop={"5px"} marginBottom={"0px"}>
                          {project.primaryClient?.name}
                        </P>
                      </Flex>
                    </Box>
                  </>
                )}
                <Box sx={{ paddingBottom: "60px" }}>
                  {/* Record nav + Update Client moved to the TOP of the rail
                      (F3a) for consistency with the PO record. See the block
                      beside the Edit action above. */}
                  {user.accessLevel !== "isProvider" && (
                    <>
                      <Divider sx={{ color: themevals.colors.inputOutline, paddingTop: "10px" }} />
                      <H5 fontFamily={"Poppins"} color={themevals.colors.black} fontSize={"14px"} fontWeight={600} marginTop={"30px"} marginBottom={"20px"}>
                        Danger Zone
                      </H5>
                      <BasicButton
                        scheme="dangerSecondary"
                        styles={{ margin: ".25rem 0.625rem .25rem 0" }}
                        type="button"
                        onClick={() => _confirmCancelProject()}
                        $iconHeight="14px"
                        $iconWidth="14px"
                        $loadingColor={themevals.colors.danger}
                        keepText={true}
                      >
                        <TrashIcon fill={"#A60000"} />
                        Cancel Project
                      </BasicButton>
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={{ paddingTop: "30px", paddingLeft: "30px", borderTop: "1px solid", borderColor: "inputOutline" }}>
                {/* F9 — same maintenance context banner the RFQ/Quote/
                    Invoice/PO records use, placed at the top of the content
                    column to match them (below the header + tab bar, not
                    spanning above the title). Self-hides for a non-WO
                    project or a client viewer; staff-gated to skip the
                    fetch. Shows on every tab. */}
                {user.accessLevel === "isCompany" && (
                  <MaintenanceContextBanner rfqId={project.originQuoteRequest?.id} showNte={false} />
                )}
                {activeTab === "overview" && <MaintenanceProjectCard project={project} />}
                {activeTab === "overview" && (
                  <Overview isCompany={user.role !== null ? true : false} project={project} reloadProject={resetProject} isProvider={user.accessLevel === "isProvider" ? true : false} providerId={user.provider ? user.provider.id : null}/>
                )}
                {activeTab === "photos" && <Photos isCompany={user.role !== null ? true : false} project={project} isProvider={user.accessLevel === "isProvider" ? true : false} />}
                {activeTab === "notes" && <Notes isCompany={user.role !== null ? true : false} project={project} />}
                {activeTab === "files" && <Files isCompany={user.role !== null ? true : false} project={project} isProvider={user.accessLevel === "isProvider" ? true : false} />}
                {activeTab === "purchase_orders" && <PurchaseOrders isCompany={user.role !== null ? true : false} project={project} isProvider={user.accessLevel === "isProvider" ? true : false} />}
                {activeTab === "dispatches" && user.role !== null && <Dispatches project={project} />}
              </Box>
            </Grid>
          </>
        )}
      </Page>
      {confirmCancelModalOpen && (
        <BasicModal
          title={`Cancel Project?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _submitCancelProject()}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              Cancel Project
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to cancel this project?</P>
        </BasicModal>
      )}
      {updateClientModalOpen && (
        <Modal isOpen={true} toggle={() => setUpdateClientModalOpen(false)} size="lg" centered={true}>
          <ModalHeader toggle={() => setUpdateClientModalOpen(false)}>Copy status update</ModalHeader>
          <ModalBody>
            <UpdateClientPanel project={project} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default Project;
